import { Fraction } from '@/sdk/entities/fractions/fraction';
import { Currency, ETHER } from '@/sdk/entities/currency';
import JSBI from 'jsbi';
import _Big from 'big.js';
import toFormat from 'toformat';
import { BigintIsh, BN_TEN, Rounding, SolidityType } from '@/sdk/constants';
import { parseBigintIsh, validateSolidityTypeInstance } from '@/sdk/utils';
import invariant from 'tiny-invariant';

const Big = toFormat(_Big);

export class CurrencyAmount extends Fraction {
  public readonly currency: Currency;

  /**
   * Helper that calls the constructor with the ETHER currency
   * @param amount ether amount in wei
   */
  public static ether(amount: BigintIsh): CurrencyAmount {
    return new CurrencyAmount(ETHER, amount);
  }

  protected constructor(currency: Currency, amount: BigintIsh) {
    const parsedAmount = parseBigintIsh(amount);
    validateSolidityTypeInstance(parsedAmount, SolidityType.uint256);

    super(parsedAmount, JSBI.exponentiate(BN_TEN, JSBI.BigInt(currency.decimals)));
    this.currency = currency;
  }

  public get raw(): JSBI {
    return this.numerator;
  }

  public toSignificant(
    significantDigits = 6,
    // eslint-disable-next-line @typescript-eslint/ban-types
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ): string {
    return super.toSignificant(significantDigits, format, rounding);
  }

  public toFixed(
    decimalPlaces: number = this.currency.decimals,
    // eslint-disable-next-line @typescript-eslint/ban-types
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ): string {
    invariant(decimalPlaces <= this.currency.decimals, 'DECIMALS');
    return super.toFixed(decimalPlaces, format, rounding);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public toExact(format: object = { groupSeparator: '' }): string {
    Big.DP = this.currency.decimals;
    return new Big(this.numerator.toString()).div(this.denominator.toString()).toFormat(format);
  }

  public isETHER() {
    return this.currency.symbol === ETHER.symbol;
  }
}
